"use client";

import type { HeaderNavMenuItem } from "@repo/ui";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

export interface MarketingNavbarLinksState {
	/** When set, replaces landing section links in the marketing navbar center menu. */
	alternateNavItems: readonly HeaderNavMenuItem[] | null;
	/** When true with {@link alternateNavItems}, inline desktop links stay hidden; dropdown shows lg+. */
	forceCollapseNavDropdown: boolean;
	/** Disables navbar center links and dropdown trigger (e.g. completion overlay). */
	navLinksDisabled: boolean;
	/** Active item id for {@link alternateNavItems} highlighting (usually current topic card id). */
	alternateNavActiveItemId: string | null;
	/** Invoked when an alternate nav item is activated (desktop or mobile sheet). */
	onAlternateNavItemActivate:
		| ((item: HeaderNavMenuItem, index: number) => void)
		| null;
}

const INITIAL_MARKETING_NAVBAR_LINKS_STATE: MarketingNavbarLinksState = {
	alternateNavItems: null,
	forceCollapseNavDropdown: false,
	navLinksDisabled: false,
	alternateNavActiveItemId: null,
	onAlternateNavItemActivate: null,
};

interface MarketingNavbarLinksContextValue extends MarketingNavbarLinksState {
	setMarketingNavbarLinks: (
		patch: Partial<MarketingNavbarLinksState>,
	) => void;
	resetMarketingNavbarLinks: () => void;
}

const MarketingNavbarLinksContext =
	createContext<MarketingNavbarLinksContextValue | null>(null);

export function MarketingNavbarLinksProvider({
	children,
}: {
	children: ReactNode;
}) {
	const [state, setState] = useState<MarketingNavbarLinksState>(
		INITIAL_MARKETING_NAVBAR_LINKS_STATE,
	);

	const setMarketingNavbarLinks = useCallback(
		(patch: Partial<MarketingNavbarLinksState>) => {
			setState((prev) => ({ ...prev, ...patch }));
		},
		[],
	);

	const resetMarketingNavbarLinks = useCallback(() => {
		setState(INITIAL_MARKETING_NAVBAR_LINKS_STATE);
	}, []);

	const value = useMemo<MarketingNavbarLinksContextValue>(
		() => ({
			...state,
			setMarketingNavbarLinks,
			resetMarketingNavbarLinks,
		}),
		[state, resetMarketingNavbarLinks, setMarketingNavbarLinks],
	);

	return (
		<MarketingNavbarLinksContext.Provider value={value}>
			{children}
		</MarketingNavbarLinksContext.Provider>
	);
}

export function useMarketingNavbarLinks(): MarketingNavbarLinksContextValue {
	const ctx = useContext(MarketingNavbarLinksContext);
	if (ctx == null) {
		throw new Error(
			"useMarketingNavbarLinks must be used within MarketingNavbarLinksProvider",
		);
	}
	return ctx;
}

/** Safe on pages without the provider (e.g. tests); returns `null` and no-ops. */
export function useMarketingNavbarLinksOptional(): MarketingNavbarLinksContextValue | null {
	return useContext(MarketingNavbarLinksContext);
}
